import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography } from '@mui/material';

import useToastPromise from '../../../../../hooks/useToast';
import dragAndDropIcon from '../../../../../styles/svg/workflowStepdotsIcon.svg';
import FormDialog from '../../../../../components/Dialog/FormDialog';
import ManageFieldsLeft from './ManageFieldsLeft';
import ManageFieldsRight from './ManageFieldsRight';
import EditFieldDialog from './EditFieldDialog';
import { useGetBoardDynamicFields } from '../../../../../reactQuery/users';
import { addDynamicFields } from '../../../../../schemas/addDynamicFields';
import { addCustomField, deleteCustomField, updateCustomField, updateCustomFieldSequence } from '../../../../../services/apiUser';
import useBoardIdByKey from '../../../../../hooks/useBoardIdByKey';
import { getResponseData } from '../../../../../reactQuery/response';

const FIELD_TYPES = [
    { value: '', label: 'Field Type' },
    { value: 'text', label: 'Textbox' },
    { value: 'multi_choice', label: 'Multiple Choice' },
    { value: 'checkbox', label: 'Multi Checkbox' },
    { value: 'select', label: 'Dropdown' },
    { value: 'date', label: 'Date' },
    { value: 'number', label: 'Number' },
];

const mapCustomFields = (customFieldData) =>
    (getResponseData(customFieldData, {})?.fields || []).map((item) => ({
        ...item,
        name: item.display_name,
        sequence: item.sequence,
        custom_field_id: item?.custom_field_id,
        field_type: item?.field_type,
    }));

const buildFieldOrder = (fields) => fields.reduce((acc, field, index) => {
    acc[field.name] = index + 1;
    return acc;
}, {});

const ManageFieldsForm = () => {
    const queryClient = useQueryClient();
    const { showToastPromise } = useToastPromise();
    const { id: paramBoardId } = useBoardIdByKey();
    const [editingFieldIndex, setEditingFieldIndex] = useState(null);
    const [confirmationDialog, setConfirmationDialog] = useState({ open: false, type: null, field: null, payload: null });
    const { data: customFieldData } = useGetBoardDynamicFields(paramBoardId);
    const methods = useForm({
        defaultValues: {
            display_name: '',
            field_type: '',
            sequence: '',
            fields: mapCustomFields(customFieldData)
        },
        resolver: yupResolver(addDynamicFields),
    });

    useEffect(() => {
        const mappedFields = mapCustomFields(customFieldData);
        methods.setValue('fields', mappedFields);
    }, [customFieldData, methods]);

    const { handleSubmit, control, watch } = methods;
    const label = watch('display_name');
    const fieldType = watch('field_type');
    const { fields, move } = useFieldArray({
        control,
        name: 'fields',
    });
    const editingField = useMemo(() => (
        editingFieldIndex === null ? null : fields?.[editingFieldIndex] || null
    ), [editingFieldIndex, fields]);

    const { mutate, isPending } = useMutation({
        mutationFn: async (data) => {
            let fieldsWithSequence = {};
            const sequence = parseInt(data.sequence.split('_')[0]);

            if (data.sequence.split('_')[1] === 'before') {
                fieldsWithSequence = fields.reduce((acc, field, index) => {
                    if (sequence === 1) {
                        acc[field.name] = index + 2;
                    } else {
                        if (index + 1 > sequence) {
                            acc[field.name] = index + 2;
                        } else {
                            acc[field.name] = index + 1;
                        }
                    }
                    return acc;
                }, {});
                fieldsWithSequence[data.display_name] = sequence === 1 ? 1 : parseInt(data.sequence.split('_')[0]) + 1;
            } else if (data.sequence.split('_')[1] === 'after') {
                fieldsWithSequence = fields.reduce((acc, field, index) => {
                    const sequence = parseInt(data.sequence.split('_')[0]);
                    if (index + 1 >= sequence) {
                        acc[field.name] = index + 2;
                    } else {
                        acc[field.name] = index + 1;
                    }
                    return acc;
                }, {});
                fieldsWithSequence[data.display_name] = parseInt(data.sequence.split('_')[0]);
            }

            const payload = data?.settings
                ? { display_name: data.display_name, field_type: data.field_type, sequence: fieldsWithSequence, settings: data.settings.map(item => (item.value)) }
                : { display_name: data.display_name, field_type: data.field_type, sequence: fieldsWithSequence };
            await showToastPromise(() => addCustomField(paramBoardId, payload), payload);
        },
        onSuccess: () => {
            methods.reset({
                display_name: '',
                field_type: '',
                settings: []
            });
            queryClient.invalidateQueries({ queryKey: ['getCustomFields', paramBoardId] });
            queryClient.invalidateQueries({ 
                predicate: (query) => query.queryKey[0] === 'card'
            });
        },
    });

    const { mutate: mutateCustomFieldSequence } = useMutation({
        mutationFn: async (data) => {
            await showToastPromise(() => updateCustomFieldSequence(paramBoardId, data));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getCustomFields', paramBoardId] });
        },
        onError: () => {
            queryClient.invalidateQueries({ queryKey: ['getCustomFields', paramBoardId] });
        },
    });

    const { mutate: mutateDeleteCustomField, isPending: isDeletingCustomField } = useMutation({
        mutationFn: async (fieldId) => {
            await showToastPromise(() => deleteCustomField(paramBoardId, fieldId));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getCustomFields', paramBoardId] });
            handleCloseConfirmationDialog();
        },
        onError: (error) => {
            // toast.error(error?.response?.data?.message);
        },
    });

    const { mutate: mutateUpdateCustomField, isPending: isUpdatingCustomField } = useMutation({
        mutationFn: async ({ customFieldId, payload }) => {
            await showToastPromise(() => updateCustomField(paramBoardId, customFieldId, payload));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getCustomFields', paramBoardId] });
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'card'
            });
            handleCloseConfirmationDialog();
            handleCloseEditField();
        },
    });

    const { mutate: mutateToggleCustomFieldVisibility, isPending: isUpdatingVisibility } = useMutation({
        mutationFn: async ({ customFieldId, payload }) => {
            await showToastPromise(() => updateCustomField(paramBoardId, customFieldId, payload));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getCustomFields', paramBoardId] });
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'card'
            });
        },
    });

    const handleOpenConfirmationDialog = (type, field, payload = null) => {
        setConfirmationDialog({
            open: true,
            type,
            field,
            payload,
        });
    };

    const handleCloseConfirmationDialog = () => {
        setConfirmationDialog({ open: false, type: null, field: null, payload: null });
    };

    const handleDeleteCustomField = (field) => {
        handleOpenConfirmationDialog('delete', field);
    };

    const handleToggleCustomFieldVisibility = (field) => {
        if (!field?.custom_field_id) {
            return;
        }

        mutateToggleCustomFieldVisibility({
            customFieldId: field.custom_field_id,
            payload: {
                display_name: field.display_name || field.name,
                field_type: field.type,
                default_value: field.default_value || '',
                settings: field.settings || [],
                hide_field: !(field.hide_field === true),
            },
        });
    };

    const handleOpenEditField = (index) => {
        setEditingFieldIndex(index);
    };

    const handleCloseEditField = () => {
        setEditingFieldIndex(null);
    };

    const handleUpdateField = (updatedField) => {
        if (editingFieldIndex === null || !editingField?.custom_field_id) {
            return;
        }

        handleOpenConfirmationDialog('edit', editingField, {
            display_name: updatedField.name,
            field_type: updatedField.type,
            default_value: editingField.default_value || '',
            settings: updatedField.settings || [],
            hide_field: editingField.hide_field ?? false,
        });
    };

    const handleConfirmAction = () => {
        if (confirmationDialog.type === 'delete' && confirmationDialog.field?.custom_field_id) {
            mutateDeleteCustomField(confirmationDialog.field.custom_field_id);
            return;
        }

        if (confirmationDialog.type === 'edit' && confirmationDialog.field?.custom_field_id && confirmationDialog.payload) {
            mutateUpdateCustomField({
                customFieldId: confirmationDialog.field.custom_field_id,
                payload: confirmationDialog.payload,
            });
        }
    };

    const confirmationDialogTitle = confirmationDialog.type === 'delete'
        ? 'Confirmation: Delete Field'
        : 'Confirmation: Update Field';
    const confirmationButtonText = confirmationDialog.type === 'delete' ? 'Delete' : 'Update';
    const confirmationButtonColor = confirmationDialog.type === 'delete' ? '#B71D18' : '#1b3e61';
    const confirmationMessage = confirmationDialog.type === 'delete'
        ? `Are you sure you want to delete ${confirmationDialog.field?.display_name || confirmationDialog.field?.name || 'this field'}?`
        : `Are you sure you want to update ${confirmationDialog.field?.display_name || confirmationDialog.field?.name || 'this field'}?`;
    const isConfirmationLoading = confirmationDialog.type === 'delete' ? isDeletingCustomField : isUpdatingCustomField;

    const onSubmit = (data) => {
        mutate(data);
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const reorderedFields = Array.from(fields);
        const [movedField] = reorderedFields.splice(result.source.index, 1);
        reorderedFields.splice(result.destination.index, 0, movedField);

        move(result.source.index, result.destination.index);
        mutateCustomFieldSequence({ sequence: JSON.stringify(buildFieldOrder(reorderedFields)) });
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="manage-fields-form-root">
                <ManageFieldsLeft
                    methods={methods}
                    label={label}
                    fieldType={fieldType}
                    FIELD_TYPES={FIELD_TYPES}
                    fields={fields}
                    isLoading={isPending}
                />
                <ManageFieldsRight
                    fields={fields}
                    onDragEnd={onDragEnd}
                    dragAndDropIcon={dragAndDropIcon}
                    handleDeleteCustomField={handleDeleteCustomField}
                    handleToggleCustomFieldVisibility={handleToggleCustomFieldVisibility}
                    isUpdatingVisibility={isUpdatingVisibility}
                    onEditField={handleOpenEditField}
                />
                <EditFieldDialog
                    open={editingFieldIndex !== null}
                    onClose={handleCloseEditField}
                    field={editingField}
                    fieldTypes={FIELD_TYPES}
                    onSubmit={handleUpdateField}
                    isLoading={isUpdatingCustomField}
                />
                {confirmationDialog.open && (
                    <FormDialog
                        open={confirmationDialog.open}
                        handleClose={handleCloseConfirmationDialog}
                        handleSubmit={handleConfirmAction}
                        buttonTxt={confirmationButtonText}
                        title={confirmationDialogTitle}
                        alignActions="center"
                        btnColor={confirmationButtonColor}
                        cancelBtn={true}
                        fullWidth={true}
                        isLoading={isConfirmationLoading}
                    >
                        <Typography className="align_text_center mb-2">
                            {confirmationMessage}
                        </Typography>
                    </FormDialog>
                )}
            </form>
        </FormProvider>
    );
};

export default ManageFieldsForm; 
